/**
 * Certificate List Page - Admin
 *
 * Admin interface to view, search, filter, and manage all issued certificates.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Filter } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Table } from '../../components/common/Table';
import { EmptyState } from '../../components/common/EmptyState';
import { useToastHelpers } from '../../components/feedback';
import { formatDate } from '../../utils/formatting';
import { getAllCertificates, getCertificateStatistics } from '../../services/mockData/certificates';
import type { CertificateStatus } from '../../types/certificate';

export const CertificateListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const { warning } = useToastHelpers();
  const certificates = getAllCertificates();
  const stats = getCertificateStatistics();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CertificateStatus | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Apply filters
  const filteredCertificates = certificates.filter((cert) => {
    const matchesSearch =
      cert.certificateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.verificationCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || cert.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredCertificates.length / itemsPerPage);
  const paginatedCertificates = filteredCertificates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadgeVariant = (status: CertificateStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'EXPIRED':
        return 'warning';
      case 'REVOKED':
      case 'SUSPENDED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewCertificate = (id: string) => {
    window.location.href = `/admin/certificates/${id}`;
  };

  const handleRevokeCertificate = (id: string) => {
    const cert = certificates.find((c) => c.id === id);
    if (window.confirm(t('admin:certificates.confirmRevoke'))) {
      // Mock API call
      warning(`Certificate ${cert?.certificateNumber} has been revoked`, 'Certificate Revoked');
    }
  };

  const columns = [
    {
      key: 'certificateNumber',
      header: t('admin:certificates.certificateNumber'),
      render: (cert: typeof certificates[0]) => (
        <div>
          <div className="font-medium text-gray-800">{cert.certificateNumber}</div>
          <div className="text-xs text-gray-500 font-mono">{cert.verificationCode}</div>
        </div>
      ),
    },
    {
      key: 'companyName',
      header: t('admin:certificates.companyName'),
      render: (cert: typeof certificates[0]) => (
        <span className="text-sm text-gray-700">{cert.companyName}</span>
      ),
    },
    {
      key: 'type',
      header: t('admin:certificates.productCategory'),
      render: (cert: typeof certificates[0]) => (
        <div>
          <div className="text-sm text-gray-700">{cert.type.replace(/_/g, ' ')}</div>
          {cert.productCategory && (
            <div className="text-xs text-gray-500">{cert.productCategory}</div>
          )}
        </div>
      ),
    },
    {
      key: 'issueDate',
      header: t('admin:certificates.issueDate'),
      render: (cert: typeof certificates[0]) => (
        <span className="text-sm text-gray-700">{formatDate(cert.issueDate)}</span>
      ),
    },
    {
      key: 'expiryDate',
      header: t('admin:certificates.expiryDate'),
      render: (cert: typeof certificates[0]) => (
        <span className="text-sm text-gray-700">{formatDate(cert.expiryDate)}</span>
      ),
    },
    {
      key: 'status',
      header: t('admin:certificates.status'),
      render: (cert: typeof certificates[0]) => (
        <Badge variant={getStatusBadgeVariant(cert.status)}>
          {cert.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('common:table.actions'),
      render: (cert: typeof certificates[0]) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleViewCertificate(cert.id)}>
            {t('admin:certificates.view')}
          </Button>
          {cert.status === 'ACTIVE' && (
            <Button variant="outline" size="sm" onClick={() => handleRevokeCertificate(cert.id)}>
              {t('admin:certificates.revoke')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      pageTitle={t('admin:certificates.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('admin:dashboard.title'), href: '/admin/dashboard' },
        { label: t('admin:certificates.title') },
      ]}
      actions={
        <Button onClick={() => (window.location.href = '/admin/certificates/issue')}>
          {t('admin:certificates.issueCertificate')}
        </Button>
      }
    >
      {/* Certificate Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:certificates.totalIssued')}</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:certificates.activeCertificates')}</p>
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:certificates.expiringSoon')}</p>
            <p className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">{t('admin:certificates.expiredRevoked')}</p>
            <p className="text-2xl font-bold text-red-600">{stats.expired + stats.revoked}</p>
          </CardBody>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin:certificates.searchCertificates')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by certificate number, company, or verification code..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin:certificates.filterByStatus')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as CertificateStatus | 'ALL')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="REVOKED">Revoked</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Certificates Table */}
      <Card>
        <CardBody>
          {filteredCertificates.length === 0 ? (
            certificates.length === 0 ? (
              <EmptyState
                icon={<Award className="w-10 h-10" />}
                title="No Certificates Issued Yet"
                description="No halal certificates have been issued yet. Certificates will appear here once applications are approved and certificates are issued."
              />
            ) : (
              <EmptyState
                icon={<Filter className="w-10 h-10" />}
                title="No Matching Certificates"
                description="No certificates match your current search and filter criteria. Try adjusting the filters or search term."
                action={{
                  label: 'Clear All Filters',
                  onClick: () => {
                    setSearchTerm('');
                    setStatusFilter('ALL');
                    setCurrentPage(1);
                  },
                }}
              />
            )
          ) : (
            <>
              <Table
                data={paginatedCertificates}
                columns={columns}
                keyExtractor={(cert) => cert.id}
                isLoading={false}
              />
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};

